import React, { useState, useEffect } from 'react';
import { Modal, Drawer, Button, Spin, message } from 'antd';
import { 
  EyeOutlined, 
  DownloadOutlined, 
  CloseOutlined,
  FullscreenOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { EquipmentReportPdfI } from '../../../interfaces';
import { ValidateCurrentToken } from '../../../helpers'; // Importar tu función de token
import { equipmentReportPdfApi } from '../../../apis'; // Importar tu instancia de axios
import "../../css/view-pdf.css";

interface PDFViewerProps {
  report: EquipmentReportPdfI | null;
  visible: boolean;
  onClose: () => void;
  isMobile?: boolean;
  onDownload?: (report: EquipmentReportPdfI) => void;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  report,
  visible,
  onClose,
  isMobile = false,
  onDownload
}) => {
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && report) {
      loadPdfForViewing();
    } else {
      // Limpiar cuando se cierra
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      setError(null);
    }

    // Cleanup al desmontar
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [visible, report]);

  const loadPdfForViewing = async () => {
    if (!report?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Usar tu sistema de token y axios
      const validateToken = ValidateCurrentToken();
      if (!validateToken) {
        throw new Error('Token inválido');
      }

      // Usar tu instancia de axios configurada
      const response = await equipmentReportPdfApi.get(`/download/${report.id}`, {
        responseType: 'blob',
        headers: {
          'Authorization': validateToken,
          'Accept': 'application/pdf'
        }
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      
    } catch (error: any) {
      console.error('Error loading PDF:', error);
      setError('No se pudo cargar el PDF');
      message.error('Error al cargar el PDF: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (report && onDownload) {
      onDownload(report);
    }
  };

  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="pdf-viewer-loading">
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            tip="Cargando PDF..."
          />
        </div>
      );
    }

    if (error) {
      return (
        <div className="pdf-viewer-error">
          <div className="error-content">
            <h3>Error al cargar el PDF</h3>
            <p>{error}</p>
            <Button type="primary" onClick={loadPdfForViewing}>
              Reintentar
            </Button>
          </div>
        </div>
      );
    }

    if (pdfUrl) {
      return (
        <div className="pdf-viewer-container">
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: isMobile ? '70vh' : '80vh' }}
            title={`Reporte ${report?.reportDate}`}
          />
        </div>
      );
    }

    return null;
  };

  const actions = [
    <Button
      key="download"
      type="primary"
      icon={<DownloadOutlined />}
      onClick={handleDownload}
      disabled={loading || !!error}
    >
      {!isMobile && 'Descargar'}
    </Button>,
    <Button
      key="newTab"
      icon={<FullscreenOutlined />}
      onClick={handleOpenInNewTab}
      disabled={loading || !!error || !pdfUrl}
    >
      {!isMobile && 'Abrir en pestaña'}
    </Button>,
    <Button
      key="close"
      danger
      icon={<CloseOutlined />}
      onClick={onClose}
    >
      {!isMobile && 'Cerrar'}
    </Button>
  ];

  if (isMobile) {
    return (
      <Drawer
        title={
          <div className="pdf-viewer-title">
            <EyeOutlined className="title-icon" />
            <span>Visualizar Reporte</span>
            {report?.reportDate && (
              <span className="title-date">
                - {new Date(report.reportDate).toLocaleDateString()}
              </span>
            )}
          </div>
        }
        placement="bottom"
        height="90%"
        open={visible}
        onClose={onClose}
        closeIcon={<CloseOutlined style={{ display: 'none' }} />}
        extra={
          <div className="pdf-viewer-actions-mobile">
            {actions}
          </div>
        }
        className="pdf-viewer-drawer"
      >
        {renderContent()}
      </Drawer>
    );
  }

  return (
    <Modal
      title={
        <div className="pdf-viewer-title">
          <EyeOutlined className="title-icon" />
          <span>Visualizar Reporte PDF</span>
          {report?.reportDate && (
            <span className="title-date">
              - {new Date(report.reportDate).toLocaleDateString()}
            </span>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ top: 20 }}
      footer={
        <div className="pdf-viewer-actions">
          {actions}
        </div>
      }
      closeIcon={<CloseOutlined style={{ display: 'none' }} />}
      className="pdf-viewer-modal"
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};